import {chain} from 'lodash';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {buildTeamPath} from '../../../helpers/modelHelpers';
import {makeMapDispatchToProps, makeMapStateToProps} from '../../../helpers/reselectHelpers';
import {InboxesFiltersEnum, TeamsFoldersEnum} from '../../../models/url/inboxesUrlModel';
import {ThunkAction} from '../../../store/appThunk';
import {getConversations} from '../../../store/data/dataSelectors';
import {getFirstTeamUserIsAdminOf} from '../../../store/data/teams/teamsSelectors';
import {getHasActionCtasBundle} from '../../../store/featureFlags/featureFlagsSelectors';
import {closeDemoInboxLoadingModalAction} from '../../../store/panels/app/appPanelActions';
import {makeGetIsDemoInboxLoadingModalOpen} from '../../../store/panels/app/appPanelSelectors';
import {AppPanelId} from '../../../store/panels/panelStateBase';
import {navigateToLinkAction} from '../../../store/stateless/navigationActions';
import {DemoInboxLoadingModal} from './demoInboxLoadingModal';

/*
 * Props.
 */

interface DemoInboxLoadingModalContainerProps {
  panelId: AppPanelId;
}

/*
 * Selectors.
 */

const getHasDemoInboxLoaded = createSelector(
  // TODO (Henry): rename legacy guided demo naming to demo inbox.
  [getHasActionCtasBundle, makeGetIsDemoInboxLoadingModalOpen(), getConversations],
  (hasActionCtasBundle, isDemoInboxLoadingModalOpen, conversations) => {
    // Avoid searching conversations if it's not necessary to do so.
    if (!hasActionCtasBundle || !isDemoInboxLoadingModalOpen) {
      return false;
    }

    const flatConversations = chain(conversations).values().compact().value();

    // The demo conversation has loaded if we have the demo conversation with a draft.
    return Boolean(flatConversations.find((c) => c.isGuidedDemo && c.lastDraft));
  },
);

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<DemoInboxLoadingModalContainerProps>()(() => ({
  isDemoInboxLoadingModalOpen: makeGetIsDemoInboxLoadingModalOpen(),
  hasDemoInboxLoaded: getHasDemoInboxLoaded,
}));

const mapDispatchToProps = makeMapDispatchToProps<DemoInboxLoadingModalContainerProps>()(({panelId}) => ({
  onLoadingComplete: (): ThunkAction => (dispatch, getState) => {
    dispatch(closeDemoInboxLoadingModalAction(panelId));

    // If our user is a team admin, navigate to the conversations for this team.
    const firstTeamUserIsAdminOf = getFirstTeamUserIsAdminOf(getState());
    if (!firstTeamUserIsAdminOf) {
      return;
    }

    const teamInboxPath = buildTeamPath(
      firstTeamUserIsAdminOf.id,
      TeamsFoldersEnum.INBOX,
      InboxesFiltersEnum.OPEN,
    );
    dispatch(navigateToLinkAction(teamInboxPath));
  },
}));

export const DemoInboxLoadingModalContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(DemoInboxLoadingModal);
